import type { ComboInstanceGroupStatus } from './comboCartInstances';
import type { ComboCartItemAllocation } from './comboCartUtils';

export interface CartDisplayItemForGrouping {
    uid: string;
    displayKey?: string;
    comboPortion?: 'combo' | 'excess';
    sku?: number;
    option_uid?: string;
    displayQuantity?: number;
    cartQuantity?: number;
    quantity?: number;
    comboDiscountQuantity?: number;
    showLooseComboParticipant?: boolean;
    comboSourceUids?: string[];
    comboInstanceId?: string;
    kitIndex?: number;
    allocationId?: string;
    grupoId?: string;
    idCampanha?: string;
    siblingQuantity?: number;
    mergedInstanceIds?: string[];
}

export interface CartComboDisplaySection<T extends CartDisplayItemForGrouping> {
    type: 'combo';
    id_campanha: string;
    comboInstanceId: string;
    kitIndex: number;
    nome_campanha: string;
    items: T[];
    order: number;
    isInstanceComplete: boolean;
    appliesDiscount: boolean;
    hasLooseComboProducts: boolean;
    completeGroupsCount: number;
    totalGroupsCount: number;
    groupsProgressLabel: string;
    kitCount?: number;
    mergedInstanceIds?: string[];
}

export interface CartStandaloneDisplaySection<T extends CartDisplayItemForGrouping> {
    type: 'standalone';
    items: [T];
    order: number;
}

export type CartDisplaySection<T extends CartDisplayItemForGrouping> =
    | CartComboDisplaySection<T>
    | CartStandaloneDisplaySection<T>;

const getPackagingQtyFromOptionUid = (option_uid?: string) => {
    const [, qtd] = option_uid?.split('_') || [];
    return Number(qtd);
};

/** Quantidade alocada ao combo para exibição no card, refletindo alocação atual do carrinho. */
export const getLiveComboDisplayPortionQuantity = (
    allocations: ComboCartItemAllocation[] | undefined,
    params: {
        sku: number;
        option_uid: string;
        cartQuantity: number;
        comboPortion?: 'combo' | 'excess';
        comboInstanceId?: string;
        mergedInstanceIds?: string[];
    }
): number => {
    const qtdEmbalagem = getPackagingQtyFromOptionUid(params.option_uid);

    const lineAllocations = (allocations ?? []).filter(
        (entry) =>
            Number(entry.cod_produto) === Number(params.sku) &&
            Number(entry.qtd_embalagem) === qtdEmbalagem &&
            entry.allocatedPackaging > 0
    );

    const allocatedTotal = lineAllocations.reduce((sum, entry) => sum + entry.allocatedPackaging, 0);

    if (params.comboPortion === 'excess') {
        return Math.max(0, params.cartQuantity - allocatedTotal);
    }

    if (!lineAllocations.length) {
        return params.cartQuantity;
    }

    const scoped = lineAllocations.filter((entry) => {
        if (params.mergedInstanceIds?.length) {
            return params.mergedInstanceIds.includes(entry.comboInstanceId ?? '');
        }
        if (params.comboInstanceId) {
            return entry.comboInstanceId === params.comboInstanceId;
        }
        return true;
    });

    return scoped.reduce((sum, entry) => sum + entry.allocatedPackaging, 0);
};

const getMergedComboDisplayItemKey = (item: CartDisplayItemForGrouping) => {
    const portion = item.comboPortion ?? 'combo';
    const grupo = item.grupoId ?? 'none';
    return `${item.sku ?? ''}-${item.option_uid ?? ''}-${portion}-${grupo}`;
};

/** Consolida linhas do mesmo produto+embalagem entre kits completos agrupados. */
export const aggregateMergedComboDisplayItems = <T extends CartDisplayItemForGrouping>(
    items: T[],
    mergedInstanceIds?: string[]
): T[] => {
    const buckets = new Map<string, T>();

    items.forEach((item) => {
        const key = getMergedComboDisplayItemKey(item);
        const displayQty = item.displayQuantity ?? item.quantity ?? 0;
        const cartQty = item.cartQuantity ?? item.quantity ?? 0;
        const existing = buckets.get(key);

        if (!existing) {
            buckets.set(key, {
                ...item,
                comboPortion: item.comboPortion ?? 'combo',
                displayQuantity: displayQty,
                cartQuantity: cartQty,
                quantity: cartQty,
                siblingQuantity: Math.max(0, cartQty - displayQty),
                comboDiscountQuantity: item.comboDiscountQuantity ?? 0,
                displayKey: `combo-merged-${key}`,
                comboSourceUids: [item.uid],
                mergedInstanceIds,
            });
            return;
        }

        const sourceUids = existing.comboSourceUids ?? [existing.uid];
        const mergedDisplayQty = (existing.displayQuantity ?? 0) + displayQty;
        const lineCartQty = Math.max(existing.cartQuantity ?? 0, cartQty);

        buckets.set(key, {
            ...existing,
            displayQuantity: mergedDisplayQty,
            cartQuantity: lineCartQty,
            quantity: lineCartQty,
            siblingQuantity: Math.max(0, lineCartQty - mergedDisplayQty),
            comboDiscountQuantity: (existing.comboDiscountQuantity ?? 0) + (item.comboDiscountQuantity ?? 0),
            comboSourceUids: sourceUids.includes(item.uid) ? sourceUids : [...sourceUids, item.uid],
            mergedInstanceIds,
        });
    });

    return Array.from(buckets.values());
};

/** Agrupa seções de kits completos do mesmo combo (com mesmo status de desconto) em uma única seção. */
export const mergeCompleteComboCartSections = <T extends CartDisplayItemForGrouping>(
    sections: CartDisplaySection<T>[]
): CartDisplaySection<T>[] => {
    const inProgressSections = sections.filter(
        (section): section is CartComboDisplaySection<T> =>
            section.type === 'combo' && !section.isInstanceComplete
    );
    const completeSections = sections.filter(
        (section): section is CartComboDisplaySection<T> =>
            section.type === 'combo' && section.isInstanceComplete
    );
    const standaloneSections = sections.filter((section) => section.type === 'standalone');

    const mergeGroups = new Map<string, CartComboDisplaySection<T>[]>();

    completeSections.forEach((section) => {
        const mergeKey = `${section.id_campanha}__${section.appliesDiscount ? 'disc' : 'no-disc'}`;
        const group = mergeGroups.get(mergeKey) ?? [];
        group.push(section);
        mergeGroups.set(mergeKey, group);
    });

    const mergedCompleteSections = Array.from(mergeGroups.values()).map((group) => {
        const first = group[0];
        const kitCount = group.length;

        if (kitCount === 1) {
            return { ...first, kitCount: 1 };
        }

        const mergedItems = aggregateMergedComboDisplayItems(
            group.flatMap((section) => section.items),
            group.map((section) => section.comboInstanceId)
        );
        const minOrder = Math.min(...group.map((section) => section.order));

        return {
            ...first,
            items: mergedItems,
            order: minOrder,
            kitCount,
            mergedInstanceIds: group.map((section) => section.comboInstanceId),
            kitIndex: 1,
        };
    });

    return [...mergedCompleteSections, ...inProgressSections, ...standaloneSections].sort(
        (a, b) => a.order - b.order
    );
};

export type ComboInstanceStatusForDisplay = {
    comboInstanceId: string;
    kitIndex: number;
    isComplete: boolean;
    appliesDiscount: boolean;
    groups: ComboInstanceGroupStatus[];
};
